import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Trash, ChevronDown, ChevronRight, Eye, EyeOff, MoveRight, Plus, X, BookOpen, ArrowUp, ArrowDown } from 'lucide-react';
import { 
  api, 
  disableSlide, 
  enableSlide, 
  moveSlide, 
  createSlideVersion, 
  createNewSlide, 
  getSysPrompts, 
  generateSlides, 
  generateSlideGuides,
  batchGenerateSideGuides,
  type SlideGuideMessage,
  type BatchSideGuideMessage 
} from '../../lib/api';
import { SlideItem } from './SlideItem';
import { CreateSlideModal } from './CreateSlideModal';
import { BatchGenerateModal } from './BatchGenerateModal';
import { BatchSideGuideModal } from './BatchSideGuideModal';
import { CheckHtmlModal } from './CheckHtmlModal';

// --- New Type Definitions --- 服务端返回的新结构
export type SlideVersion = {
  id: number;
  version_number: string;
  html_code: string;
  side_guide_content: string;
  side_guide: string;
  audit: string;
  is_final: boolean;
  created_by: number;
  created_by_username: string;
  updated_at: string;
};

export type Slide = {
  id: number;
  slide_index: number;
  latest_version: string;
  versions: SlideVersion[];
};

export type PlanVersion = {
  id: number;
  version_number: string;
  created_by_username: string;
};

// 定义 WebSocket 客户端接口
// 兼容 generateSlides 和 generateSlideGuides 的返回值
type WSClient = {
  // start 方法参数定义为可选或联合类型以兼容两种 API
  // generateSlides: (sysPromptId, slideCount, startIndex)
  // generateSlideGuides: (slideCount, startIndex)
  start: (...args: any[]) => void; 
  stop: () => void;
  close: () => void;
  getReadyState: () => number;
};

type Props = {
  slides: Slide[];
  plans: PlanVersion[];
  onEnter: (mode: 'ware', id: number) => void;
  onDeleteSlideVersion: (slideId: number, versionId: number) => void;
  onPlanVersionChange: (planId: number) => void;
  currentUserId: number;
  currentUserRole: string;
  planId: number;
  onSlideStatusChange: () => void;
  lessonInfo?: {
    id: number;
    title: string;
    sort_order: number;
  };
  stageInfo?: {
    title: string;
  };
};

export const SlideList: React.FC<Props> = ({ slides, plans, onEnter, onDeleteSlideVersion, onPlanVersionChange, currentUserId, currentUserRole, planId, onSlideStatusChange, lessonInfo, stageInfo }) => {
  // 1. 初始化教案版本选择
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(() => {
    const globalSavedPlanId = localStorage.getItem('globalSelectedPlanId');
    if (globalSavedPlanId) {
      return parseInt(globalSavedPlanId);
    }
    return plans[0]?.id || 0;
  });
  
  // 2. 状态管理
  const [newIndexes, setNewIndexes] = useState<Record<number, string>>({});
  const [movingSlideId, setMovingSlideId] = useState<number | null>(null);
  
  const [isCreateSlideModalOpen, setIsCreateSlideModalOpen] = useState(false);
  const [newSlideIndex, setNewSlideIndex] = useState('');
  const [isCreatingSlide, setIsCreatingSlide] = useState(false);
  
  // 3. 批量生成相关状态
  const [isBatchGenerateModalOpen, setIsBatchGenerateModalOpen] = useState(false);
  const [isBatchSideGuideModalOpen, setIsBatchSideGuideModalOpen] = useState(false);
  const [isCheckHtmlModalOpen, setIsCheckHtmlModalOpen] = useState(false);
  const [generateMode, setGenerateMode] = useState<'slides' | 'guides'>('slides');
  
  const [wsConnectionStatus, setWsConnectionStatus] = useState<'not_connected' | 'connecting' | 'connected' | 'disconnected'>('not_connected');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [prompts, setPrompts] = useState<Array<{ id: number; name: string; category: string }>>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState<string[]>([]);
  const [slideCount, setSlideCount] = useState<number>(22);
  const [startIndex, setStartIndex] = useState<number>(0); // 开始位置，默认为0
  
  // 4. 历史记录弹窗状态
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{
    id: number;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at: string;
    input_tokens: number;
    output_tokens: number;
    input_money?: number;
    output_money?: number;
  }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  
  // 🔥 5. WebSocket 引用
  const wsRef = useRef<WSClient | null>(null);
  
  // 6. 获取聊天记录
  const fetchChatHistory = async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    
    try {
      // 获取token
      const getToken = (): string | null => {
        // 先从sessionStorage获取
        const sessionToken = sessionStorage.getItem('token');
        if (sessionToken) {
          return sessionToken;
        }
        
        // 再从localStorage（带过期时间）获取
        const localStorageToken = localStorage.getItem('token');
        if (localStorageToken) {
          try {
            const parsedToken = JSON.parse(localStorageToken);
            // 检查是否有过期时间
            if (parsedToken.expiry) {
              const now = new Date().getTime();
              if (now > parsedToken.expiry) {
                // 已过期，清除token
                localStorage.removeItem('token');
                return null;
              }
              return parsedToken.value;
            }
            // 旧格式的token，直接返回
            return localStorageToken;
          } catch (error) {
            // 解析失败，可能是旧格式的token
            return localStorageToken;
          }
        }
        
        return null;
      };
      
      const token = getToken();
      if (!token) {
        throw new Error('未找到登录令牌');
      }
      
      // 调用接口
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/slides/plans/${selectedPlanVersion}/chat-history/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`获取历史记录失败: ${response.status}`);
      }
      
      const data = await response.json();
      setChatHistory(data);
    } catch (error) {
      console.error('获取聊天记录失败:', error);
      setHistoryError(error instanceof Error ? error.message : '获取历史记录失败，请稍后重试');
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // 7. 打开历史记录弹窗
  const handleOpenHistoryModal = () => {
    fetchChatHistory();
    setIsHistoryModalOpen(true);
  };
  
  // 8. 关闭历史记录弹窗
  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
  };

  // 打开批量生成模态框的处理函数
  const handleOpenBatchModal = (mode: 'slides' | 'guides') => {
    setGenerateMode(mode);
    setIsBatchGenerateModalOpen(true);
  };

  // --- WebSocket 自动连接逻辑 ---
  useEffect(() => {
    // 当模态框打开时，尝试建立连接
    if (isBatchGenerateModalOpen) {
      // 如果已经有连接，先关闭
      if (wsRef.current) {
        wsRef.current.close();
      }

      setWsConnectionStatus('connecting');
      setGeneratedSlides([]); // 清空旧日志
      
      try {
        // 消息处理回调
        const handleMessage = (message: SlideGuideMessage) => {
          console.log('📩 [WebSocket] Message:', message.type);
          
          if (message.type === 'connected') {
            setWsConnectionStatus('connected');
            setGeneratedSlides(prev => [...prev, `✅ [系统] ${message.content}`]);
          } else if (message.type === 'progress') {
            setGeneratedSlides(prev => [...prev, `🔄 [进度] ${message.content}`]);
          } else if (message.type === 'chunk') {
            // 流式内容，这里简单追加
            // setGeneratedSlides(prev => [...prev, message.content]); 
          } else if (message.type === 'all_done') {
            setIsGenerating(false);
            setGeneratedSlides(prev => [...prev, `🎉 [完成] ${message.content}`]);
            alert(generateMode === 'slides' ? '幻灯片生成完成！' : '教案生成完成！');
            onSlideStatusChange(); // 刷新列表
          } else if (message.type === 'error') {
            console.error('WebSocket Error Message:', message.content);
            setGeneratedSlides(prev => [...prev, `❌ [错误] ${message.content}`]);
            if (message.content === 'WebSocket connection error' || message.content === 'WebSocket connection closed unexpectedly') {
                setWsConnectionStatus('disconnected');
                setIsGenerating(false);
            }
          } else if (message.type === 'stopped') {
            setIsGenerating(false);
            setGeneratedSlides(prev => [...prev, `🛑 [系统] ${message.content}`]);
          }
        };

        let wsClient: WSClient;

        // 根据模式连接不同的接口
        if (generateMode === 'slides') {
          wsClient = generateSlides(selectedPlanVersion, slideCount, startIndex, handleMessage) as WSClient;
        } else {
          // 'guides' 模式调用 generateSlideGuides
          // 注意：generateSlideGuides 不需要 slideCount 作为连接参数，只需要 startIndex
          // 但 start 指令可能需要
          wsClient = generateSlideGuides(selectedPlanVersion, startIndex, handleMessage) as WSClient;
        }
        
        wsRef.current = wsClient;
        
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        setWsConnectionStatus('disconnected');
        setIsGenerating(false);
      }
    } else {
      // 当模态框关闭时，断开连接
      if (wsRef.current) {
        console.log('🔌 [WebSocket] Closing connection due to modal close');
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnectionStatus('not_connected');
      setIsGenerating(false);
    }
    
    // 组件卸载清理
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isBatchGenerateModalOpen, selectedPlanVersion, slideCount, startIndex, generateMode]); 

  // --- 获取系统提示词 ---
  useEffect(() => {
    if (isBatchGenerateModalOpen) {
      const fetchPrompts = async () => {
        try {
          const res = await getSysPrompts();
          // 根据模式过滤提示词 (可选优化：如果教案生成也需要特定类别的提示词)
          // 目前假设都用 'gen_slide' 或显示全部，这里暂时保持原逻辑
          // 获取所有系统提示词，让BatchGenerateModal内部根据模式过滤
          setPrompts(res.data.map((p: any) => ({ 
            id: p.id, 
            name: p.name, 
            category: p.category 
          })));
          
          // 根据默认生成模式设置初始选中的提示词
          const initialPrompts = res.data.filter((p: any) => p.category === 'gen_slide');
          if (initialPrompts.length > 0 && !selectedPromptId) {
            const latestPrompt = initialPrompts.reduce((prev: any, curr: any) => 
              prev.id > curr.id ? prev : curr
            );
            setSelectedPromptId(latestPrompt.id);
          }
        } catch (error) {
          console.error('获取系统提示词失败:', error);
        }
      };
      fetchPrompts();
    }
  }, [isBatchGenerateModalOpen]);

  // --- 调试辅助函数 ---
  const debugWebSocketConnection = () => {
    console.log('🔍 Debug Info:');
    console.log('Mode:', generateMode);
    console.log('Status:', wsConnectionStatus);
    console.log('Ref Current:', wsRef.current);
    if (wsRef.current) {
        console.log('ReadyState:', wsRef.current.getReadyState());
    }
  };
  
  // --- Sync Plan Selection with LocalStorage ---
  React.useEffect(() => {
    if (plans.length > 0) {
      const globalSavedPlanId = localStorage.getItem('globalSelectedPlanId');
      if (globalSavedPlanId) {
        const parsedPlanId = parseInt(globalSavedPlanId);
        const planExists = plans.some(plan => plan.id === parsedPlanId);
        if (planExists) {
          setSelectedPlanVersion(parsedPlanId);
        } else {
          const firstPlanId = plans[0]?.id || 0;
          setSelectedPlanVersion(firstPlanId);
          localStorage.setItem('globalSelectedPlanId', firstPlanId.toString());
        }
      } else {
        const firstPlanId = plans[0]?.id || 0;
        setSelectedPlanVersion(firstPlanId);
        localStorage.setItem('globalSelectedPlanId', firstPlanId.toString());
      }
    }
  }, [plans]);
  
  React.useEffect(() => {
    if (selectedPlanVersion) {
      localStorage.setItem('globalSelectedPlanId', selectedPlanVersion.toString());
    }
  }, [selectedPlanVersion]);
  
  React.useEffect(() => {
    if (plans.length > 0) {
      const savedPlanId = parseInt(localStorage.getItem('globalSelectedPlanId') || '0');
      const planExists = plans.some(plan => plan.id === savedPlanId);
      if (planExists && savedPlanId !== selectedPlanVersion) {
        onPlanVersionChange(savedPlanId);
      }
    }
  }, []);
  
  // --- Slide Expansion Logic ---
  const slideExpansionKey = useMemo(() => {
    const slideIds = slides.map(slide => slide.id).sort().join('_');
    return `expandedSlides_${selectedPlanVersion}_${slideIds || 'default'}`;
  }, [selectedPlanVersion, slides]);
  
  const [expandedSlideId, setExpandedSlideId] = useState<number | null>(() => {
    const savedExpanded = localStorage.getItem(slideExpansionKey);
    return savedExpanded ? parseInt(savedExpanded) : null;
  });
  
  React.useEffect(() => {
    const savedExpanded = localStorage.getItem(slideExpansionKey);
    setExpandedSlideId(savedExpanded ? parseInt(savedExpanded) : null);
  }, [slideExpansionKey]);

  const sortedSlides = useMemo(() => {
    return [...slides].sort((a, b) => a.slide_index - b.slide_index);
  }, [slides]);

  const maxEnabledSlideIndex = useMemo(() => {
    const enabledSlides = slides.filter(slide => slide.slide_index < 1000);
    if (enabledSlides.length === 0) return 0;
    return Math.max(...enabledSlides.map(slide => slide.slide_index));
  }, [slides]);

  const toggleSlideExpansion = (slideId: number) => {
    setExpandedSlideId(prev => {
      const newExpandedId = prev === slideId ? null : slideId;
      localStorage.setItem(slideExpansionKey, newExpandedId?.toString() || '');
      return newExpandedId;
    });
  };
  
  const handlePlanVersionChange = (planId: number) => {
    setSelectedPlanVersion(planId);
    localStorage.setItem('globalSelectedPlanId', planId.toString());
    onPlanVersionChange(planId);
  };

  const canManageSlide = (slide: Slide): boolean => {
    const highestVersion = slide.versions.find(
      (version) => version.version_number === slide.latest_version
    );
    return highestVersion?.created_by === currentUserId;
  };

  const isSlideDisabled = (slideIndex: number): boolean => {
    return slideIndex >= 1000;
  };

  const handleDisableSlide = async (slide: Slide) => {
    try {
      await disableSlide(planId, slide.slide_index);
      onSlideStatusChange(); 
    } catch (error) {
      console.error('禁用幻灯片失败:', error);
    }
  };

  const handleEnableSlide = async (slide: Slide) => {
    try {
      await enableSlide(planId, slide.slide_index);
      onSlideStatusChange(); 
    } catch (error) {
      console.error('启用幻灯片失败:', error);
    }
  };
  
  const handleNewIndexChange = (slideId: number, value: string) => {
    setNewIndexes(prev => ({ ...prev, [slideId]: value }));
  };
  
  const handleMoveSlide = async (slide: Slide) => {
    try {
      const newIndexStr = newIndexes[slide.id] || slide.slide_index.toString();
      const newIndex = parseInt(newIndexStr, 10);
      
      if (isNaN(newIndex) || newIndex < 0 || newIndex >= 1000) {
        alert('新序号必须是非负数且小于1000');
        return;
      }
      if (newIndex === slide.slide_index) {
        alert('新序号与当前序号相同');
        return;
      }
      if (newIndex > maxEnabledSlideIndex) {
        alert(`新序号不能超过当前最大序号 ${maxEnabledSlideIndex}`);
        return;
      }
      
      setMovingSlideId(slide.id);
      await moveSlide(planId, slide.slide_index, newIndex);
      onSlideStatusChange();
      setNewIndexes(prev => {
        const { [slide.id]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('移动幻灯片失败:', error);
      alert('移动幻灯片失败，请重试');
    } finally {
      setMovingSlideId(null);
    }
  };
  
  const handleCreateNewVersion = async (slide: Slide) => {
    try {
      const latestVersion = slide.versions.find(
        (version) => version.version_number === slide.latest_version
      );
      if (!latestVersion) {
        alert('无法找到最新版本，请重试');
        return;
      }
      const versionParts = latestVersion.version_number.replace(/^v/, '').split('.').map(Number);
      versionParts[versionParts.length - 1] += 1; 
      const newVersionNumber = `v${versionParts.join('.')}`;
      
      await createSlideVersion({
        slide_index: slide.slide_index,
        html_code: latestVersion.html_code,
        side_guide_content: latestVersion.side_guide_content,
        side_guide: latestVersion.side_guide,
        audit: latestVersion.audit,
        is_final: false,
        plan_id: planId,
        version_number: newVersionNumber
      });
      onSlideStatusChange();
    } catch (error) {
      console.error('创建新幻灯片版本失败:', error);
      alert('创建新幻灯片版本失败，请重试');
    }
  };
  
  const handleCreateSlide = async () => {
    try {
      const slideIndex = parseInt(newSlideIndex, 10);
      if (isNaN(slideIndex) || slideIndex < 0 || slideIndex >= 1000) {
        alert('幻灯片序号必须是非负数且小于1000');
        return;
      }
      setIsCreatingSlide(true); 
      await createNewSlide({
        slide_index: slideIndex,
        plan_id: planId
      });
      setIsCreateSlideModalOpen(false);
            setNewSlideIndex('');
      onSlideStatusChange();
    } catch (error) {
      console.error('创建新幻灯片失败:', error);
      alert('创建新幻灯片失败，请重试');
    } finally {
      setIsCreatingSlide(false); 
    }
  };
  
  const handleOpenCreateSlideModal = () => {
    setNewSlideIndex((maxEnabledSlideIndex + 1).toString());
    setIsCreateSlideModalOpen(true);
  };
  
  const handleCloseCreateSlideModal = () => {
    setIsCreateSlideModalOpen(false);
    setNewSlideIndex('');
  };

  return (
    <div className="p-4 bg-white">
      {/* 1. 教案版本选择和批量生成按钮 */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">教案版本</label>
            <select
              value={selectedPlanVersion}
              onChange={(e) => {
                const planId = parseInt(e.target.value);
                handlePlanVersionChange(planId);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {plans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.version_number} - {plan.created_by_username}
                </option>
              ))}
            </select>
          </div>
          
          {(() => {
            const isPlanCreator = currentUserId !== undefined;
            return isPlanCreator && (
              <div className="flex gap-2 self-end">
                <button
                  onClick={() => setIsCheckHtmlModalOpen(true)}
                  className="h-10 px-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  检查HTML合规
                </button>
                <button
                  onClick={() => setIsBatchSideGuideModalOpen(true)}
                  className="h-10 px-4 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <BookOpen size={16} />
                  批量生成每页教案
                </button>
              </div>
            );
          })()}
        </div>
      </div>
      
      {/* 新建slide按钮 */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={handleOpenCreateSlideModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          新建幻灯片
        </button>
      </div>

      {/* 2. Slide列表 */}
      <div className="divide-y divide-gray-100">
        
        <CreateSlideModal
          isOpen={isCreateSlideModalOpen}
          onClose={handleCloseCreateSlideModal}
          newSlideIndex={newSlideIndex}
          setNewSlideIndex={setNewSlideIndex}
          onCreateSlide={handleCreateSlide}
          isCreatingSlide={isCreatingSlide}
          maxEnabledSlideIndex={maxEnabledSlideIndex}
        />
        
        {/* 批量生成模态框 - 根据 generateMode 动态调整行为 */}
        <BatchGenerateModal
          isOpen={isBatchGenerateModalOpen}
          onClose={() => setIsBatchGenerateModalOpen(false)}
          plans={plans}
          selectedPlanVersion={selectedPlanVersion}
          lessonInfo={lessonInfo}
          stageInfo={stageInfo}
          wsConnectionStatus={wsConnectionStatus}
          prompts={prompts}
          selectedPromptId={selectedPromptId}
          setSelectedPromptId={setSelectedPromptId}
          isGenerating={isGenerating}
          generatedSlides={generatedSlides}
          slideCount={slideCount}
          setSlideCount={setSlideCount}
          startIndex={startIndex}
          setStartIndex={setStartIndex}
          // [NEW] 传入当前模式
          generateMode={generateMode} 
          
          onStartGeneration={() => {
            if (!wsRef.current) {
              alert('服务器未连接，请关闭窗口后重试');
              return;
            }

            // 仅在 slides 模式下检查 promptId
            if (generateMode === 'slides' && !selectedPromptId) {
              alert('请先选择一个系统提示词');
              return;
            }
            
            setIsGenerating(true);
            const actionText = generateMode === 'slides' ? '课件' : '教案';
            setGeneratedSlides(prev => [...prev, `🚀 [指令] 发送开始生成${actionText}指令 (Count: ${slideCount}, Start: ${startIndex})`]);
            
            // 根据模式调用不同的 start 方法参数
            if (generateMode === 'slides') {
               // generateSlides 接口需要 sysPromptId
               if (selectedPromptId) {
                  wsRef.current.start(selectedPromptId, slideCount, startIndex);
               }
            } else {
               // generateSlideGuides 接口不需要 sysPromptId，参数顺序为 (slideCount, startIndex)
               wsRef.current.start(slideCount, startIndex);
            }
          }}
          
          onStopGeneration={() => {
            if (wsRef.current) {
              setGeneratedSlides(prev => [...prev, `🛑 [指令] 发送停止指令`]);
              wsRef.current.stop();
            }
          }}
          debugWebSocketConnection={debugWebSocketConnection}
        />
        
        <BatchSideGuideModal
          isOpen={isBatchSideGuideModalOpen}
          onClose={() => setIsBatchSideGuideModalOpen(false)}
          planId={selectedPlanVersion}
          lessonInfo={lessonInfo}
          stageInfo={stageInfo}
          plans={plans}
          onSlideStatusChange={onSlideStatusChange}
        />
        
        <CheckHtmlModal
          isOpen={isCheckHtmlModalOpen}
          onClose={() => setIsCheckHtmlModalOpen(false)}
          planId={selectedPlanVersion}
        />
        
        {/* 历史记录弹窗 */}
        {isHistoryModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-100">
            <div className="bg-white rounded-lg shadow-xl p-6 w-[90vw] h-[90vh] overflow-hidden flex flex-col">
              {/* 历史记录弹窗头部 */}
              <div className="flex items-center justify-between mb-4 border-b pb-4">
                <h3 className="text-xl font-medium text-gray-900">生成历史记录</h3>
                <button
                  onClick={handleCloseHistoryModal}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </button>
              </div>
              
              {/* 历史记录内容 */}
              <div className="flex-1 overflow-auto p-2">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : historyError ? (
                  <div className="text-center text-red-600 py-8">
                    <p>{historyError}</p>
                    <button
                      onClick={fetchChatHistory}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      重试
                    </button>
                  </div>
                ) : chatHistory.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    暂无聊天记录
                  </div>
                ) : (
                  <div className="space-y-6">
                    {chatHistory.map((message) => (
                      <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-4 rounded-lg ${message.role === 'user' ? 'bg-blue-500 text-white rounded-br-none' : message.role === 'assistant' ? 'bg-gray-100 text-gray-800 rounded-bl-none' : 'bg-green-100 text-green-800 rounded-bl-none'}`}>
                          <div className="text-sm font-medium mb-1">
                            {message.role === 'user' ? '您' : message.role === 'assistant' ? 'AI助手' : '系统'}
                          </div>
                          <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                          <div className="flex justify-between items-center mt-2 text-xs opacity-70">
                            <span>{new Date(message.created_at).toLocaleString()}</span>
                            {message.input_tokens > 0 || message.output_tokens > 0 ? (
                              <span className="font-mono inline-flex items-center gap-0.5">
                                <ArrowUp size={10} className="text-green-500" />{message.input_tokens ?? 0}
                                {' / '}
                                <ArrowDown size={10} className="text-orange-500" />{message.output_tokens ?? 0}
                                {' / '}
                                💰¥{((message.input_money ?? 0) + (message.output_money ?? 0)).toFixed(4)}
                                {' / '}
                                🏦¥{(message.total_money ?? 0).toFixed(4)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* 历史记录弹窗底部 */}
              <div className="flex justify-end mt-4 border-t pt-4">
                <button
                  onClick={handleCloseHistoryModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        )}
        
        {sortedSlides.map(slide => {
            const disabled = isSlideDisabled(slide.slide_index);
            const canManage = canManageSlide(slide);
            
            return (
              <SlideItem
                key={slide.id}
                slide={slide}
                expandedSlideId={expandedSlideId}
                toggleSlideExpansion={toggleSlideExpansion}
                onEnter={onEnter}
                onDeleteSlideVersion={onDeleteSlideVersion}
                canManage={canManage}
                isDisabled={disabled}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                newIndexes={newIndexes}
                handleNewIndexChange={handleNewIndexChange}
                handleMoveSlide={handleMoveSlide}
                movingSlideId={movingSlideId}
                handleCreateNewVersion={handleCreateNewVersion}
                handleDisableSlide={handleDisableSlide}
                handleEnableSlide={handleEnableSlide}
              />
            );
        })}
      </div>
    </div>
  );
};

